import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Send, Loader2, User as UserIcon, ShoppingBag, BookOpen, AlertTriangle } from "lucide-react";
import SlaProgressBar from "@/components/SlaProgressBar";

export default function StaffSoporteDetalle() {
  const { user } = useAuth();
  const isSuper = user?.roles?.includes("superadmin_dcc");
  const { ticket_id } = useParams();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/staff/tickets/${ticket_id}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [ticket_id]);

  const assign = async (toMe = true) => {
    try {
      await api.post(`/staff/tickets/${ticket_id}/assign${toMe ? `?assignee_id=${user.id}` : ""}`);
      toast.success("Asignado.");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/staff/tickets/${ticket_id}/messages`, {
        body: reply.trim(), is_internal_note: internal,
      });
      setReply(""); setInternal(false);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const setStatus = async (status) => {
    try {
      await api.put(`/staff/tickets/${ticket_id}/status`, { status });
      toast.success("Estado actualizado.");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  if (!data) return <StaffLayout user={user} superadmin={isSuper}><div className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div></StaffLayout>;
  const t = data.ticket;
  const ctx = data.context || {};

  return (
    <StaffLayout user={user} superadmin={isSuper}>
      <Link to="/app/staff/soporte" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white mb-3">
        <ArrowLeft className="w-3 h-3" /> Volver a la cola
      </Link>

      <div className="grid lg:grid-cols-[1fr_320px] gap-4">
        <div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-gray-500">{t.ticket_number}</span>
              <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 rounded bg-white/10 text-gray-300">{t.priority}</span>
              <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 rounded bg-white/10 text-gray-300">{t.status}</span>
              {t.sla_breached && (
                <span className="inline-flex items-center gap-1 text-[10px] text-red-300 font-bold" data-testid="sla-breach-flag">
                  <AlertTriangle className="w-3 h-3" /> SLA breach
                </span>
              )}
            </div>
            <h1 className="mt-2 text-2xl font-bold text-white font-display">{t.subject}</h1>
            <p className="mt-1 text-xs text-gray-500">Alumno: {t.user_name} ({t.user_email})</p>
          </div>

          <div className="mt-4">
            <SlaProgressBar ticket={t} />
          </div>

          <div className="mt-4 flex flex-wrap gap-2" data-testid="ticket-actions">
            {!t.assigned_to_user_id && (
              <button onClick={() => assign(true)} data-testid="ticket-assign-me"
                      className="inline-flex items-center gap-1 rounded-md bg-[#F26A21] text-[#0B0F14] px-3 py-1.5 text-xs font-bold">
                Asignarme
              </button>
            )}
            {t.status !== "resolved" && t.status !== "closed" && (
              <button onClick={() => setStatus("resolved")} data-testid="ticket-mark-resolved"
                      className="inline-flex items-center gap-1 rounded-md bg-emerald-500 text-[#0B0F14] px-3 py-1.5 text-xs font-bold">
                Marcar resuelto
              </button>
            )}
            {t.status === "resolved" && (
              <button onClick={() => setStatus("closed")}
                      className="inline-flex items-center gap-1 rounded-md border border-white/20 text-white px-3 py-1.5 text-xs">
                Cerrar
              </button>
            )}
          </div>

          <div className="mt-4 space-y-2" data-testid="ticket-thread">
            {data.messages.map((m) => {
              const staff = m.sender_type === "staff";
              const isInternal = m.is_internal_note;
              return (
                <div key={m.id} className={`rounded-2xl border p-4 ${isInternal ? "border-amber-500/40 bg-amber-500/5" : staff ? "border-[#F26A21]/40 bg-[#F26A21]/5" : "border-white/10 bg-white/5"}`}>
                  <p className={`text-[10px] uppercase tracking-widest font-bold ${isInternal ? "text-amber-300" : staff ? "text-[#F26A21]" : "text-gray-400"}`}>
                    {m.sender_name} · {staff ? "Staff" : "Alumno"}{isInternal && " · Nota interna"}
                  </p>
                  <p className="mt-1 text-sm text-white whitespace-pre-wrap">{m.body}</p>
                  <p className="mt-1 text-[10px] text-gray-500">{new Date(m.created_at).toLocaleString("es-MX")}</p>
                </div>
              );
            })}
          </div>

          <form onSubmit={send} className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-3" data-testid="ticket-reply-form">
            <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3}
                      data-testid="ticket-reply-input"
                      className="w-full rounded-md bg-transparent border-0 text-sm text-white placeholder:text-gray-500 outline-none"
                      placeholder="Escribe respuesta al alumno o nota interna…" />
            <div className="flex items-center justify-between mt-1">
              <label className="inline-flex items-center gap-1 text-xs text-gray-400">
                <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)}
                       data-testid="ticket-internal-toggle" className="accent-amber-500" />
                Nota interna (no visible al alumno)
              </label>
              <button type="submit" disabled={sending || !reply.trim()}
                      data-testid="ticket-reply-send"
                      className="inline-flex items-center gap-1 rounded-md bg-[#F26A21] text-[#0B0F14] px-3 py-1.5 text-xs font-bold disabled:opacity-50">
                {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Enviar
              </button>
            </div>
          </form>
        </div>

        <aside className="lg:sticky lg:top-4 self-start rounded-2xl border border-white/10 bg-white/5 p-4 space-y-3" data-testid="ticket-context">
          <h3 className="text-xs uppercase tracking-widest text-gray-400 font-bold inline-flex items-center gap-1">
            <UserIcon className="w-3 h-3" /> Contexto del alumno
          </h3>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gray-500 inline-flex items-center gap-1">
              <ShoppingBag className="w-3 h-3" /> Últimas órdenes
            </p>
            <ul className="mt-1 text-xs text-gray-300 space-y-0.5">
              {ctx.recent_orders?.length ? ctx.recent_orders.map(o => (
                <li key={o.id}>· {o.order_number} · <span className="text-gray-400">{o.status}</span> · ${o.total_mxn?.toLocaleString("es-MX")}</li>
              )) : <li className="text-gray-500">Sin órdenes</li>}
            </ul>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gray-500 inline-flex items-center gap-1">
              <BookOpen className="w-3 h-3" /> Inscripciones activas
            </p>
            <ul className="mt-1 text-xs text-gray-300 space-y-0.5">
              {ctx.enrollments?.length ? ctx.enrollments.map((e, i) => (
                <li key={i}>· {e.program_id?.slice(0, 8) || "—"} · <span className="text-gray-400">{e.status}</span></li>
              )) : <li className="text-gray-500">Sin inscripciones</li>}
            </ul>
          </div>
        </aside>
      </div>
    </StaffLayout>
  );
}
